import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, MapPin, CheckCircle2, Clock3, CalendarDays, Repeat2 } from "lucide-react";
import api from "@/lib/api";
import useGeoCurrency, { priceLabel } from "@/lib/useGeoCurrency";
import { optimizeImageUrl } from "@/lib/imageUtils";
import SEOHead from "@/components/SEOHead";
import ServiceForm from "@/components/ServiceForm";

export default function VisaDetail() {
  const { slug } = useParams();
  const [visa, setVisa] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = useGeoCurrency();

  useEffect(() => {
    api.get(`/visas/${slug}`).then((res) => setVisa(res.data)).catch(() => setVisa(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={32} /></div>;
  if (!visa) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">Visa not found.</div>;

  const infoCards = [
    { icon: Clock3, label: "Processing Time", value: visa.processing_time },
    { icon: CalendarDays, label: "Duration of Stay", value: visa.duration },
    { icon: CalendarDays, label: "Validity", value: visa.validity },
    { icon: Repeat2, label: "Entry Type", value: visa.entry_type },
  ].filter((c) => c.value);

  return (
    <div data-testid="visa-detail-page" className="bg-white pb-16 sm:pb-24">
      <SEOHead pageKey={`visa-${slug}`} />
      <div className="relative h-[26vh] sm:h-[38vh] min-h-[200px]">
        {visa.banner_image ? (
          <img src={optimizeImageUrl(visa.banner_image, 1400)} alt={visa.name} fetchPriority="high" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#1A1A1A]" />
        )}
        <div className="absolute inset-0 bg-[#1A1A1A]/55 flex flex-col items-center justify-center text-center px-4">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)] flex items-center gap-1.5"><MapPin size={12} /> {visa.country}</span>
          <h1 data-testid="visa-detail-title" className="font-heading text-3xl sm:text-4xl text-white mt-2">{visa.name}</h1>
          <span className="text-white/90 text-sm mt-2">{visa.visa_type}</span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {infoCards.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {infoCards.map((c, i) => (
                <div key={i} className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB]">
                  <p className="text-xs text-[var(--brand-secondary)] flex items-center gap-1"><c.icon size={12} /> {c.label}</p>
                  <p className="text-sm font-medium text-[#1A1A1A] mt-1">{c.value}</p>
                </div>
              ))}
            </div>
          )}

          {visa.description && (
            <div>
              <h2 className="font-heading text-2xl text-[#1A1A1A] mb-3">Visa Details</h2>
              <p data-testid="visa-description" className="text-[var(--brand-secondary)] leading-relaxed whitespace-pre-line">{visa.description}</p>
            </div>
          )}

          {visa.requirements?.length > 0 && (
            <div data-testid="visa-requirements">
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Required Documents</h3>
              <ul className="space-y-2">
                {visa.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--brand-secondary)]"><CheckCircle2 size={15} className="text-green-600 shrink-0 mt-0.5" /> {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="p-4 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB] mb-4">
              <p className="font-heading text-xl text-[#1A1A1A]">Apply for this Visa</p>
              {visa.price_inr > 0 && <p data-testid="visa-detail-price" className="text-xl font-price text-[var(--brand-primary)] mt-1">{priceLabel(visa.price_inr, visa.price_usd, currency)}</p>}
              <p className="text-xs text-[var(--brand-secondary)] mt-1">
                {visa.requires_payment ? "Payment required to process this visa." : "No upfront payment needed - our team will contact you on WhatsApp."}
              </p>
            </div>
            <ServiceForm service={visa} entityType="visa" />
          </div>
        </div>
      </div>
    </div>
  );
}
